import type { XrefPumps } from "@/composables/forms/formTypes";
import { PoundsPerCubicFoot } from "@/libs/units/density";
import { GenericUnits } from "@/libs/units/generic";
import { LengthUnits } from "@/libs/units/length";
import { PercentageUnits } from "@/libs/units/percentage";
import {
  computePumpLabel,
  computePumpStageType
} from "@/models/equipment/pumps/properties";
import { arrayOfInts } from "@/utils/utils";
import type { RenderScenarioExportCols } from "./sizingExportTypes";
import {
  getPowerPerStageBHP,
  getPressurePerStageHeadFt
} from "@/libs/calculations/pumps/pumpData";
import { getPumpEfficiencyRatio } from "@/libs/calculations/pumps";

export const renderPumpCols: RenderScenarioExportCols = ({
  scenario,
  calcs,
  targetUnits,
  maxPumps
}) => {
  const pumpRows = arrayOfInts(maxPumps).flatMap(i => {
    const section = `PUMP ${i + 1}`;
    const pump: XrefPumps[number] | undefined =
      calcs.descendingOrderPumps.value[i];

    if (!pump?.r_pump) {
      return [];
    }

    const freqRatio = scenario?.parameters?.value?.pump_hz / 60;
    const averageFluidDensitySpGr = PoundsPerCubicFoot.toStandardUnit(
      calcs.pumpCalculations.aggregate.averageFluidDensityLBMFT3.value
    );
    const pumpStages = pump?.total_stages || 1;

    const pressurePerStageHeadFtBEP = getPressurePerStageHeadFt(
      pump?.r_pump?.bep_rate_standard_pump_bpd || 0,
      pump,
      1,
      freqRatio,
      1
    );

    const powerPerStageBrakeHPBEP = getPowerPerStageBHP(
      pump?.r_pump?.bep_rate_standard_pump_bpd || 0,
      pump,
      freqRatio,
      averageFluidDensitySpGr,
      scenario.parameters.value?.pump_wear_brake_horsepower_correction_ratio
    );

    const pumpEfficiencyBEP = getPumpEfficiencyRatio(
      // flowRateSTBD
      pump?.r_pump?.bep_rate_standard_pump_bpd || 0,
      // totalDynamicHeadFt
      pressurePerStageHeadFtBEP * pumpStages,
      // fluidDensitySpGr
      averageFluidDensitySpGr,
      // breakHp
      powerPerStageBrakeHPBEP * pumpStages
    );
    return [
      {
        section,
        label: "Stage Type",
        targetUnit: null,
        value: computePumpStageType(pump?.r_pump)
      },
      {
        section,
        label: "Housing (Stages)",
        targetUnit: null,
        value: pump?.r_xref_housings
          .map(
            xrefHousing =>
              `${xrefHousing.r_housing?.housing_number} (${xrefHousing.stage_count})`
          )
          .join(" - ")
      },
      {
        section,
        label: "# of Stages",
        targetUnit: null,
        value: pump?.total_stages
      },
      {
        section,
        label: "Model",
        targetUnit: null,
        value: computePumpLabel(pump?.r_pump, pump?.use_high_strength_shaft)
      },
      {
        section,
        label: "Pump OD",
        targetUnit: LengthUnits.Inches,
        value: pump?.r_pump?.housing_outer_diameter_inches,
        disableConversion: true
      },
      {
        section,
        label: "Pump Length",
        targetUnit: targetUnits.largeLengthUnit,
        value: pump?.length_feet
      },
      {
        section,
        label: "Pump Weight",
        targetUnit: targetUnits.massUnit,
        value: pump?.weight_pounds
      },
      {
        section,
        label: "Lift per Stage @ BEP",
        targetUnit: targetUnits.largeLengthUnit,
        value: pressurePerStageHeadFtBEP
      },
      {
        section,
        label: calcs.isScenarioEspShore.value
          ? "Lift per Stage @ Operating Point"
          : "Lift per Stage @ Duty Point",
        targetUnit: targetUnits.largeLengthUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]?.liftPerStageHeadFt
      },
      {
        section,
        label: "BHP per stage @ BEP",
        targetUnit: targetUnits.hydraulicPowerUnit,
        value: powerPerStageBrakeHPBEP
      },
      {
        section,
        label: calcs.isScenarioEspShore.value
          ? "BHP per stage @ Operating Point"
          : "BHP per stage @ Duty Point",
        targetUnit: targetUnits.hydraulicPowerUnit,
        value: calcs.pumpCalculations.individual.value[pump?.id]?.bhpPerStageHP
      },
      {
        section,
        label: "Pump Efficiency @ BEP",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(pumpEfficiencyBEP)
      },
      {
        section,
        label: calcs.isScenarioEspShore.value
          ? "Pump Efficiency @ Operating Point"
          : "Pump Efficiency @ Duty Point",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.pumpCalculations.individual.value[pump?.id]?.efficiencyRatio
        )
      },
      {
        section,
        label: "Std Shaft Rating @ 3500 RPM",
        targetUnit: targetUnits.hydraulicPowerUnit,
        value: pump?.r_pump?.standard_shaft_rating_hp
      },
      {
        section,
        label: calcs.isScenarioEspShore.value
          ? "Std Shaft Rating @ Operating Frequency"
          : "Std Shaft Rating @ Duty Point Frequency",
        targetUnit: targetUnits.hydraulicPowerUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.shaftPowerLimitStandardHP
      },
      {
        section,
        label: "HS Shaft Rating @ 3500 RPM",
        targetUnit: targetUnits.hydraulicPowerUnit,
        value: pump?.r_pump?.high_strength_shaft_rating_hp
      },
      {
        section,
        label: calcs.isScenarioEspShore.value
          ? "HS Shaft Rating @ Operating Frequency"
          : "HS Shaft Rating @ Duty Point Frequency",
        targetUnit: targetUnits.hydraulicPowerUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.shaftPowerLimitHighStrengthHP
      },
      {
        section,
        label: "% load on Std Shaft",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.standardShaftRatingRatio
        )
      },
      {
        section,
        label: "% load on High Strength Shaft",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.highStrengthShaftRatingRatio
        )
      },
      {
        section,
        label: "Operating Range Min",
        targetUnit: targetUnits.liquidFlowUnit,
        value: calcs.pumpCalculations.individual.value[pump?.id]?.rorMin
      },
      {
        section,
        label: "Operating Range Max",
        targetUnit: targetUnits.liquidFlowUnit,
        value: calcs.pumpCalculations.individual.value[pump?.id]?.rorMax
      },
      {
        section,
        label: "Total Flow Rate at Pump Intake",
        overrideLabel: { newLabel: "bbl/d", labelToReplace: "STB/D" },
        targetUnit: targetUnits.liquidFlowUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.intakeStageCalculations?.intakeFluidPropertiesContext
            .flowRatesInSitu?.fluidFlowRateBBLD
      },
      {
        section,
        label: "Total Flow Rate at Pump Discharge",
        overrideLabel: { newLabel: "bbl/d", labelToReplace: "STB/D" },
        targetUnit: targetUnits.liquidFlowUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.dischargeStageCalculations?.dischargeFluidPropertiesContext
            .flowRatesInSitu?.fluidFlowRateBBLD
      },
      {
        section,
        label: "Intake Temperature",
        targetUnit: targetUnits.tempUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.intakeStageCalculations?.intakeTemperatureF
      },
      {
        section,
        label: "Discharge Temperature",
        targetUnit: targetUnits.tempUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.intakeStageCalculations?.dischargeTemperatureF
      },
      {
        section,
        label: "Intake Viscosity",
        targetUnit: targetUnits.viscosityUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.intakeStageCalculations?.intakeFluidPropertiesContext
            .fluidProperties.liquidViscosityCP
      },
      {
        section,
        label: "Discharge Viscosity",
        targetUnit: targetUnits.viscosityUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.dischargeStageCalculations?.dischargeFluidPropertiesContext
            .fluidProperties.liquidViscosityCP
      },
      {
        section,
        label: "Free Gas @ Intake",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.pumpCalculations.individual.value[pump?.id]?.gasAtIntakeRatio
        )
      },
      {
        section,
        label: "Free Gas @ Discharge",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.pumpCalculations.individual.value[pump?.id]?.gasAtDischargeRatio
        )
      },
      {
        section,
        label: "Flow Modifier",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(pump?.rate_correction_ratio)
      },
      {
        section,
        label: "Head Modifier",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(pump?.head_correction_ratio)
      },
      {
        section,
        label: "Power Modifier",
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          pump?.brake_horsepower_correction_ratio
        )
      },
      {
        section,
        label: "Pump Operating RPM",
        targetUnit: GenericUnits.RevolutionsPerMinute,
        value: pump && calcs.pumpCalculations.aggregate.operatingRPM
      },
      {
        section,
        label: calcs.isScenarioEspShore.value
          ? "Thrust Load"
          : "Thrust Load at Duty Point",
        targetUnit: targetUnits.massUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]?.operatingThrustLoad
      },
      {
        section,
        label: calcs.isScenarioEspShore.value
          ? "Shut-in Thrust Load"
          : "Max Thrust Load",
        targetUnit: targetUnits.massUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]?.shutInThrustLoad
      },
      {
        section,
        label: "Shut-In Pressure @ Discharge",
        targetUnit: targetUnits.pressureUnit,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]?.shutInPressurePSIA
      },
      {
        section,
        label: "Shut-in Burst Pressure % Std Housing",
        targetUnit: PercentageUnits.Percentage,
        hidden: !calcs.isScenarioEspShore.value,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.shutInBurstRatioStandard
        )
      },
      {
        section,
        label: "Burst Pressure Rating",
        targetUnit: targetUnits.pressureUnit,
        hidden: !calcs.isScenarioEspShore.value,
        value: pump?.r_pump?.standard_housing_pressure_rating_psi
      },
      {
        section,
        label: "Operating Burst Pressure % Std Housing",
        targetUnit: PercentageUnits.Percentage,
        hidden: !calcs.isScenarioEspShore.value,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.operatingBurstRatioStandard
        )
      },
      {
        section,
        label: "Max Housing Pressure",
        targetUnit: targetUnits.pressureUnit,
        hidden: calcs.isScenarioEspShore.value,
        value:
          calcs.pumpCalculations.individual.value[pump?.id]
            ?.maxHousingPressurePSIA
      }
    ];
  });

  const aggregateSection = "PUMP TOTALS";
  const aggregateRows = !calcs.isScenarioEspShore.value
    ? [
        {
          section: aggregateSection,
          label: "Total Max HP",
          targetUnit: targetUnits.hydraulicPowerUnit,
          value: calcs.pumpCalculations.aggregate.totalMaxBrakeHP.value
        },
        ...(calcs.ascendingOrderPumps.value.length > 1
          ? [
              {
                section: aggregateSection,
                label: "Taper Total Head Ft",
                targetUnit: targetUnits.largeLengthUnit,
                value:
                  calcs.pumpCalculations.aggregate.taperTotalOperatingHeadFeet
                    .value
              },
              {
                section: aggregateSection,
                label: "Total Max Head Ft",
                targetUnit: targetUnits.largeLengthUnit,
                value: calcs.pumpCalculations.aggregate.totalMaxHeadFeet.value
              },
              {
                section: aggregateSection,
                label: "Max Pressure @ Discharge",
                targetUnit: targetUnits.pressureUnit,
                value:
                  calcs.pumpCalculations.aggregate.totalMaxDischargePressurePSIA
                    .value
              }
            ]
          : [])
      ]
    : [];

  return [...pumpRows, ...aggregateRows];
};
